import { Select } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useCallback, useMemo, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuizCreateData } from '../../hooks/useQuizCreateData'
import styles from '../../styles/admin/StudyContent.module.scss'
import BaseInput from '../common/BaseInput'
import Label from '../common/Label'
import SaveButton from '../common/SaveButton'
import SaveResultModal from '../common/SaveResultModal'
import Table from '../common/Table'
import CurriculumHeader from './CurriculumHeader'
import CurriculumItem from './CurriculumItem'
import QuizContainer from './QuizContainer'
import QuizTypeA from './QuizTypeA'
import QuizTypeC from './QuizTypeC'
import { notifications } from '@mantine/notifications'

const QuizCreateContent = ({ fromPage,level }) => {
  const navigate = useNavigate()
  const [opened, { open, close }] = useDisclosure(false)
  const [saveStatus, setSaveStatus] = useState(null)
  const [answerType, setAnswerType] = useState('text')
  const [questionLabelInput, setQuestionLabelInput] = useState('')
  const [currentQuestionLabel, setCurrentQuestionLabel] = useState('')
  const [autoSetQueue, setAutoSetQueue] = useState({})
  const [currentAutoStep, setCurrentAutoStep] = useState('none')

  const {
    curriculum,
    quizData,
    isLoading,

    levelOptions,
    mainOptions,
    subOptions,
    unitOptions,
    detailUnitOptions,

    handleLevelChange,
    handleMainChapterChange,
    handleSubChapterChange,
    handleUnitChange,
    handleDetailUnitChange,
    handleCountChange,
    handleQuestionChange,
    handleQuestionImageChange,
    handleSubQuestionImageChange,
    handleAnswerImageFilesChange,
    handleTypeChange,
    handleCorrectAnswerChange,
    handleCorrectAnswersChange,
    handleCheckedStatesChange,
    handleExplanationChange,
    handleExplanationImageChange,
    handleSaveQuiz,
    handleQuestionLabelChange: handleQuestionLabelChangeFromHook,
    updateQuestionLabel,
  } = useQuizCreateData()

  useEffect(() => {}, [curriculum, currentQuestionLabel])

  const generateQuestionLabel = useCallback(() => {
    if (
      !curriculum.level ||
      !curriculum.mainChapter ||
      !curriculum.subChapter ||
      !curriculum.unit ||
      !curriculum.detailUnit
    ) {
      return ''
    }

    const levelNum =
      curriculum.level
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const mainNum =
      curriculum.mainChapter
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const subNum =
      curriculum.subChapter
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const unitNum =
      curriculum.unit
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'
    const detailNum =
      curriculum.detailUnit
        .toString()
        .replace(/[^0-9]/g, '')
        .slice(-1) || '0'

    const questionLabel = `${levelNum}${mainNum}${subNum}${unitNum}${detailNum}`

    return questionLabel
  }, [
    curriculum.level,
    curriculum.mainChapter,
    curriculum.subChapter,
    curriculum.unit,
    curriculum.detailUnit,
  ])

  useEffect(() => {
    const newQuestionLabel = generateQuestionLabel()
    if (newQuestionLabel && newQuestionLabel !== currentQuestionLabel) {
      setCurrentQuestionLabel(newQuestionLabel)

      if (handleQuestionLabelChangeFromHook) {
        handleQuestionLabelChangeFromHook(newQuestionLabel)
      }

      if (updateQuestionLabel) {
        updateQuestionLabel(newQuestionLabel)
      }
    }
  }, [
    curriculum.level,
    curriculum.mainChapter,
    curriculum.subChapter,
    curriculum.unit,
    curriculum.detailUnit,
    generateQuestionLabel,
    currentQuestionLabel,
    handleQuestionLabelChangeFromHook,
    updateQuestionLabel,
  ])

  const findOptionValue = useCallback((options, searchValue) => {
    if (!options || options.length === 0) {
      return null
    }

    const firstItem = options[0]

    if (typeof firstItem === 'object' && firstItem !== null) {
      let found = options.find(
        (opt) => opt.value === searchValue || opt.label === searchValue
      )
      if (found) return found.value
      found = options.find((opt) => opt.depth === searchValue)
      if (found) return found.value

      found = options.find(
        (opt) =>
          (opt.label && opt.label.toString().includes(searchValue)) ||
          (opt.value && opt.value.toString().includes(searchValue))
      )
      if (found) return found.value
      found = options.find((opt) => {
        const labelNumbers = opt.label ? opt.label.toString().match(/\d+/g) : []
        const valueNumbers = opt.value ? opt.value.toString().match(/\d+/g) : []
        const depthMatch = opt.depth === searchValue
        return (
          depthMatch ||
          (labelNumbers && labelNumbers.includes(searchValue)) ||
          (valueNumbers && valueNumbers.includes(searchValue))
        )
      })
      return found ? found.value : null
    }

    if (typeof firstItem === 'string') {
      if (options.includes(searchValue)) {
        return searchValue
      }
      const numericMatch = options.find((opt) => {
        const numbers = opt.match(/\d+/g)
        return numbers && numbers.includes(searchValue)
      })
      if (numericMatch) {
        return numericMatch
      }
      const partialMatch = options.find(
        (opt) =>
          opt.includes(searchValue) ||
          opt.toLowerCase().includes(searchValue.toLowerCase()) ||
          opt.endsWith(` ${searchValue}`) ||
          opt.endsWith(`${searchValue}`)
      )

      return partialMatch || null
    }

    return null
  }, [])

  useEffect(() => {
    if (currentAutoStep === 'none' || Object.keys(autoSetQueue).length === 0)
      return

    switch (currentAutoStep) {
      case 'level':
        if (autoSetQueue.level) {
          const levelValue = findOptionValue(levelOptions, autoSetQueue.level)
          if (levelValue) {
            handleLevelChange(levelValue)
            setCurrentAutoStep('main')
          }
        }
        break

      case 'main':
        if (autoSetQueue.main && mainOptions.length > 0) {
          const mainValue = findOptionValue(mainOptions, autoSetQueue.main)
          if (mainValue) {
            handleMainChapterChange(mainValue)
            setCurrentAutoStep('sub')
          } else {
            setCurrentAutoStep('none')
            setAutoSetQueue({})
          }
        }
        break

      case 'sub':
        if (autoSetQueue.sub && subOptions.length > 0) {
          const subValue = findOptionValue(subOptions, autoSetQueue.sub)
          if (subValue) {
            handleSubChapterChange(subValue)
            setCurrentAutoStep('unit')
          } else {
            setCurrentAutoStep('none')
            setAutoSetQueue({})
          }
        }
        break

      case 'unit':
        if (autoSetQueue.unit && unitOptions.length > 0) {
          const unitValue = findOptionValue(unitOptions, autoSetQueue.unit)
          if (unitValue) {
            handleUnitChange(unitValue)
            setCurrentAutoStep('detailUnit')
          } else {
            setCurrentAutoStep('none')
            setAutoSetQueue({})
          }
        }
        break

      case 'detailUnit':
        if (autoSetQueue.detailUnit && detailUnitOptions.length > 0) {
          const detailUnitValue = findOptionValue(
            detailUnitOptions,
            autoSetQueue.detailUnit
          )
          if (detailUnitValue) {
            handleDetailUnitChange(detailUnitValue)

            setTimeout(() => {
              const finalQuestionLabel = generateQuestionLabel()

              if (finalQuestionLabel) {
                setCurrentQuestionLabel(finalQuestionLabel)

                if (handleQuestionLabelChangeFromHook) {
                  handleQuestionLabelChangeFromHook(finalQuestionLabel)
                }

                if (updateQuestionLabel) {
                  updateQuestionLabel(finalQuestionLabel)
                }
              }
            }, 100)
          }
          setCurrentAutoStep('none')
          setAutoSetQueue({})
        }
        break
    }
  }, [
    currentAutoStep,
    autoSetQueue,
    levelOptions,
    mainOptions,
    subOptions,
    unitOptions,
    detailUnitOptions,
    findOptionValue,
    handleLevelChange,
    handleMainChapterChange,
    handleSubChapterChange,
    handleUnitChange,
    handleDetailUnitChange,
    generateQuestionLabel,
    handleQuestionLabelChangeFromHook,
  ])

  const parseQuestionLabel = useCallback((input) => {
    const numbers = input.replace(/[^0-9]/g, '')

    let level, main, sub, unit, detailUnit

    if (numbers.length === 5) {
      // 5자리: 51111 (각각 1자리씩)
      ;[level, main, sub, unit, detailUnit] = numbers.split('')
    } else if (numbers.length === 6) {
      // 6자리: 991111 (레벨 2자리, 나머지 1자리씩)
      level = numbers.substring(0, 2)
      main = numbers.charAt(2)
      sub = numbers.charAt(3)
      unit = numbers.charAt(4)
      detailUnit = numbers.charAt(5)
    } else {
      // 5자리나 6자리가 아니면 처리하지 않음
      return
    }

    setAutoSetQueue({
      level,
      main,
      sub,
      unit,
      detailUnit,
    })

    setCurrentAutoStep('level')
  }, [])

  const handleQuestionLabelChange = useCallback((value) => {
    // 타입 제거
    setQuestionLabelInput(value)
  }, [])

  const handleQuestionLabelKeyDown = useCallback(
    (e) => {
      // 타입 제거
      if (e.key === 'Enter') {
        e.preventDefault()
        parseQuestionLabel(questionLabelInput)
      }
    },
    [parseQuestionLabel, questionLabelInput]
  )

  const multipleAnswerQuizProps = useMemo(
    () => ({
      question: quizData.question,
      correctAnswers: [
        quizData.correctAnswer || quizData.answer1 || '',
        quizData.answer2 || '',
        quizData.answer3 || '',
        quizData.answer4 || '',
        quizData.answer5 || '',
      ],
      checkedStates: [
        quizData.isCorrect1 || false,
        quizData.isCorrect2 || false,
        quizData.isCorrect3 || false,
        quizData.isCorrect4 || false,
        quizData.isCorrect5 || false,
      ],
      explanation: quizData.explanation,
      explanationImageFile: quizData.explanationImageFile,
      onQuestionChange: handleQuestionChange,
      onCorrectAnswersChange: handleCorrectAnswersChange,
      onCheckedStatesChange: handleCheckedStatesChange,
      onExplanationChange: handleExplanationChange,
      onExplanationImageChange: handleExplanationImageChange,
    }),
    [
      quizData,
      handleQuestionChange,
      handleCorrectAnswersChange,
      handleCheckedStatesChange,
      handleExplanationChange,
      handleExplanationImageChange,
    ]
  )

  const curriculumData = useMemo(
    () => [
      {
        level: (
          <Select
            placeholder="Level"
            data={levelOptions}
            value={curriculum.level}
            onChange={handleLevelChange}
            disabled={isLoading}
          />
        ),
        mainChapter: (
          <Select
            placeholder="Main Chapter"
            data={mainOptions}
            value={curriculum.mainChapter}
            onChange={handleMainChapterChange}
            disabled={isLoading}
          />
        ),
        subChapter: (
          <Select
            placeholder="Sub Chapter"
            data={subOptions}
            value={curriculum.subChapter}
            onChange={handleSubChapterChange}
            disabled={isLoading}
          />
        ),
        unit: (
          <Select
            placeholder="Unit"
            data={unitOptions}
            value={curriculum.unit}
            onChange={handleUnitChange}
            disabled={isLoading}
          />
        ),
        detailUnit: (
          <Select
            placeholder="Detail Unit"
            data={detailUnitOptions}
            value={curriculum.detailUnit}
            onChange={handleDetailUnitChange}
            disabled={isLoading}
          />
        ),
        no: (
          <BaseInput
            value={curriculum.count}
            placeholder="0"
            onChange={(e) => handleCountChange(e.target.value)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={3}
            disabled={isLoading}
            style={{
              height: '36px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
            }}
          />
        ),
        contentType: <Label text="Quiz" />,
        questionLabel: (
          <input
            value={questionLabelInput}
            placeholder={
              currentQuestionLabel || curriculum.questionLabel || '51111'
            }
            onChange={(e) => handleQuestionLabelChange(e.target.value)}
            onKeyDown={handleQuestionLabelKeyDown}
            type="text"
            disabled={isLoading}
            style={{
              height: '36px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              width: '80px',
              padding: '0 8px',
              fontSize: '14px',
            }}
          />
        ),
        type: (
          <Select
            placeholder={'Type'}
            data={['A', 'C']}
            value={curriculum.selectedType}
            onChange={handleTypeChange}
          />
        ),
      },
    ],
    [
      curriculum,
      currentQuestionLabel,
      questionLabelInput,
      levelOptions,
      mainOptions,
      subOptions,
      unitOptions,
      detailUnitOptions,
      handleLevelChange,
      handleMainChapterChange,
      handleSubChapterChange,
      handleUnitChange,
      handleDetailUnitChange,
      handleCountChange,
      handleQuestionLabelChange,
      handleQuestionLabelKeyDown,
      isLoading,
      handleTypeChange,
    ]
  )

  const handleSaveWithModal = useCallback(async () => {
    if (!curriculum.level) {
      notifications.show({
        message: 'Please select a Level.',
        color: 'red',
      })
      return
    }
    if (!curriculum.mainChapter) {
      notifications.show({
        message: 'Please select a Main Chapter.',
        color: 'red',
      })
      return
    }
    if (!curriculum.subChapter) {
      notifications.show({
        message: 'Please select a Sub Chapter.',
        color: 'red',
      })
      return
    }
    if (!curriculum.unit) {
      notifications.show({
        message: 'Please select a Unit.',
        color: 'red',
      })
      return
    }
    if (!curriculum.detailUnit) {
      notifications.show({
        message: 'Please select a Detail Unit.',
        color: 'red',
      })
      return
    }
    if (!curriculum.count?.trim() || Number(curriculum.count) <= 0) {
      notifications.show({
        message: 'Please enter a valid count (greater than 0).',
        color: 'red',
      })
      return
    }
    if (!curriculum.selectedType) {
      notifications.show({
        message: 'Please select a Type.',
        color: 'red',
      })
      return
    }
    if (!quizData.question?.trim()) {
      notifications.show({
        message: 'Please enter a question.',
        color: 'red',
      })
      return
    }

    const hasCheckedAnswer = [
      quizData.isCorrect1,
      quizData.isCorrect2,
      quizData.isCorrect3,
      quizData.isCorrect4,
      quizData.isCorrect5,
    ].some(Boolean)
    if (!hasCheckedAnswer) {
      notifications.show({
        message: 'Please check at least one correct answer.',
        color: 'red',
      })
      return
    }

    const latestQuestionLabel = generateQuestionLabel()

    try {
      if (latestQuestionLabel) {
        if (handleQuestionLabelChangeFromHook) {
          handleQuestionLabelChangeFromHook(latestQuestionLabel)
        }

        if (updateQuestionLabel) {
          updateQuestionLabel(latestQuestionLabel)
        }
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      await handleSaveQuiz(answerType)

      setSaveStatus({ type: 'success', message: 'Saved successfully' })
      open()

    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Failed to save. Please try again.'
      setSaveStatus({ type: 'error', message: errorMessage })
      open()
    }
  }, [
    handleSaveQuiz,
    open,
    navigate,
    answerType,
    generateQuestionLabel,
    curriculum,
    quizData,
    currentQuestionLabel,
    handleQuestionLabelChangeFromHook,
    updateQuestionLabel,
  ])

  const handleModalClose = useCallback(() => {
    close()
    setSaveStatus(null)
  }, [close])

  const handleModalConfirm = useCallback(() => {
    if (saveStatus?.type === 'success') {
      close();
      setSaveStatus(null);
      navigate('/lectures', { state: { fromPage, level } });
    } else {
      close();
      setSaveStatus(null);
    }
  }, [saveStatus, close, navigate, fromPage, level]); // ✅ saveStatus 의존성 추가

  const renderQuizTypeComponent = useMemo(() => {
    switch (curriculum.selectedType) {
      case 'A':
        return (
          <QuizTypeA
            {...multipleAnswerQuizProps}
            questionImageFile={quizData.questionImageFile}
            onQuestionImageChange={handleQuestionImageChange}
            existingQuestionImageUrl=""
            subQuestionImageFile={quizData.subQuestionImageFile}
            onSubQuestionImageChange={handleSubQuestionImageChange}
            existingSubQuestionImageUrl=""
            existingExplanationImageUrl=""
            onExistingQuestionImageRemove={() => {}}
            onExistingSubQuestionImageRemove={() => {}}
            onExistingExplanationImageRemove={() => {}}
            answerImageFiles={quizData.answerImageFiles}
            onAnswerImageFilesChange={handleAnswerImageFilesChange}
            answerType={answerType}
          />
        )

      case 'C':
        return (
          <QuizTypeC
            {...multipleAnswerQuizProps}
            questionImageFile={quizData.questionImageFile}
            onQuestionImageChange={handleQuestionImageChange}
            existingQuestionImageUrl=""
            existingExplanationImageUrl=""
            onExistingQuestionImageRemove={() => {}}
            onExistingExplanationImageRemove={() => {}}
          />
        )

      default:
        return (
          <QuizTypeA
            {...multipleAnswerQuizProps}
            questionImageFile={quizData.questionImageFile}
            onQuestionImageChange={handleQuestionImageChange}
            existingQuestionImageUrl=""
            subQuestionImageFile={quizData.subQuestionImageFile}
            onSubQuestionImageChange={handleSubQuestionImageChange}
            existingSubQuestionImageUrl=""
            existingExplanationImageUrl=""
            onExistingQuestionImageRemove={() => {}}
            onExistingSubQuestionImageRemove={() => {}}
            onExistingExplanationImageRemove={() => {}}
            answerImageFiles={quizData.answerImageFiles}
            onAnswerImageFilesChange={handleAnswerImageFilesChange}
            answerType={answerType}
          />
        )
    }
  }, [
    curriculum.selectedType,
    multipleAnswerQuizProps,
    quizData.questionImageFile,
    quizData.subQuestionImageFile,
    quizData.answerImageFiles,
    handleQuestionImageChange,
    handleSubQuestionImageChange,
    handleAnswerImageFilesChange,
    answerType,
  ])

  return (
    <QuizContainer>
      <p
        style={{
          color: '#0D2A4F',
          fontWeight: '700',
          fontSize: '20px',
          marginBottom: '20px',
          marginTop: '5px',
        }}
      >
        Lectures & Assignments
      </p>

      <Table className={styles.tableWrapper}>
        <thead>
          <CurriculumHeader />
        </thead>
        <tbody>
          {curriculumData.map((item, index) => (
            <CurriculumItem key={index} curriculum={item} index={index} />
          ))}
        </tbody>
      </Table>

      {curriculum.selectedType === 'A' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <p
            style={{
              color: '#111',
              fontSize: '15px',
              fontWeight: 700,
              margin: 0,
              marginLeft: '14px',
            }}
          >
            Answer Type
          </p>
          <Select
            data={[
              { value: 'text', label: 'Text' },
              { value: 'image', label: 'Image' },
            ]}
            value={answerType}
            onChange={(value) => setAnswerType(value)}
            className={styles.answerTypeSelector}
            w="100%"
          />
        </div>
      )}

      {renderQuizTypeComponent}

      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <SaveButton onClick={handleSaveWithModal}>
          {isLoading ? 'Saving...' : 'Save'}
        </SaveButton>
      </div>

      <SaveResultModal
        opened={opened}
        onClose={handleModalClose}
        status={saveStatus}
        onConfirm={handleModalConfirm}
      />
    </QuizContainer>
  )
}

export default QuizCreateContent
